import { useCallback, useMemo, useState } from "react";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { useLocation, useNavigate } from "react-router";
import { processError } from "../../../../common/utils/base_error";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useChangeHistory } from "../../../../common/hooks/use_change_history";
import { LogTipoTabela } from "../../../../common/utils/log_tabela_tipo";
import { UsuariosCadastroForm, UsuariosCadastroFormDefaultValues, UsuariosCadastroFormSchema } from "../schemas/usuarios_cadastro_form";
import { Usuario } from "../../domain/models/usuario";
import { DropDown } from "../../../../common/domain/models/dropdown";
import { GridRowSelectionModel } from "@mui/x-data-grid";
import { GrupoAcessoUsuario } from "../../domain/models/grupo_acesso_usuario";
import { CentroCustoRestricaoUsuario } from "../../domain/models/centro_custo_restricao_usuario";
import { useUsuariosCadastroPagination } from "../hooks/usuario_cadastro/pagination";
import { useUserAuthStore } from "../../../../stores/global/userAuth";

export const useUsuariosCadastroViewModel = () => {
    const {
        administracao: {
            getUsuarioByIdUseCase,
            deleteUsuarioUseCase,
            createUsuarioUseCase,
            updateUsuarioUseCase,
            getGrupoAcessoUsuarioUseCase,
            getCentroCustoRestricaoUsuarioUseCase,
        },
        commons: {
            getAllLogAlteracaoUseCase,
            getAllLogAlteracaoByParentUseCase,
            getFuncionariosDropDownUseCase,
        },
    } = useDependencyInjection();

    const changeHistory = useChangeHistory({
        getAllLogAlteracaoUseCase,
        getAllLogAlteracaoByParentUseCase,
        onSetError: (e) => pushErrorNotification("Erro", [e]),
    });

    const hookForm = useForm<UsuariosCadastroForm>({
        mode: "onSubmit",
        reValidateMode: "onSubmit",
        defaultValues: UsuariosCadastroFormDefaultValues,
        resolver: zodResolver(UsuariosCadastroFormSchema),
    });

    const location = useLocation();
    const navigate = useNavigate();
    const user = useUserAuthStore();

    const { pushErrorNotification, pushWarnNotification, pushInfoNotification, setPageLoading } = usePageLoader();

    const [ddlFuncionario, setDdlFuncionario] = useState<DropDown[]>();

    const [selectedIdUsuario, setSelectedIdUsuario] = useState<number>();
    const [selectedUsuario, setSelectedUsuario] = useState<Usuario>();

    const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string>();

    const [centroCustoRestrito] = hookForm.watch(['centroCustoRestrito']);
    const [senha] = hookForm.watch(['senha']);

    const pagination = useUsuariosCadastroPagination();

    const [grupoAcessoUsuarioLista, setGrupoAcessoUsuarioLista] = useState<GrupoAcessoUsuario[]>();
    const [restricaoCentroCustoUsuarioLista, setRestricaoCentroCustoUsuarioLista] = useState<CentroCustoRestricaoUsuario[]>();

    const [grupoAcessoSelectionModel, setGrupoAcessoSelectionModel] =
        useState<GridRowSelectionModel>();

    const [centroCustoRestritoSelectionModel, setCentroCustoRestritoSelectionModel] =
        useState<GridRowSelectionModel>();

    const statusOptions = useMemo(() => {
        return [
            {
                id: 0,
                label: 'Ativo',
                disabled: false,
                style: {
                    backgroundColor: '#45b6af',
                    color: 'white',
                    padding: '.2rem .4rem',
                    fontSize: '1.3rem',
                    fontWeight: 700,
                    textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000',
                    borderRadius: '3px',

                },
            },
            {
                id: 1,
                label: 'Bloqueado',
                disabled: false,
                style: {
                    backgroundColor: '#f3565d',
                    color: 'white',
                    padding: '.2rem .4rem',
                    fontSize: '1.3rem',
                    fontWeight: 700,
                    textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000',
                    borderRadius: '3px',
                },
            },
        ];
    }, []);

    //#region CARREGAMENTO.

    const loadCentroCustoRestritoLista = useCallback(async (funcState?: { page?: number, rowsPerPage?: number }, idsCentroCusto?: number[]) => {
        try {
            setPageLoading(true);
            await getCentroCustoRestricaoUsuarioUseCase.execute(funcState?.page, funcState?.rowsPerPage);
            setRestricaoCentroCustoUsuarioLista(getCentroCustoRestricaoUsuarioUseCase.items);
            pagination.centroCustoRestricaoUsuarioSetter.setTotalItems(getCentroCustoRestricaoUsuarioUseCase.totalItems);

            if (idsCentroCusto && idsCentroCusto.length > 0) {
                setCentroCustoRestritoSelectionModel({ type: 'include', ids: new Set(idsCentroCusto) });
            }
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar centros de custo restritos !'));
        } finally {
            setPageLoading(false);
        }
    }, [
        getCentroCustoRestricaoUsuarioUseCase,
        pagination,
        pushErrorNotification,
        setPageLoading,
    ]);

    const loadGrupoAcessoUsuario = useCallback(async (funcState?: { page?: number, rowsPerPage?: number }, idsGrupoAcesso?: number[]) => {
        try {
            setPageLoading(true);
            await getGrupoAcessoUsuarioUseCase.execute(funcState?.page, funcState?.rowsPerPage);

            setGrupoAcessoUsuarioLista(getGrupoAcessoUsuarioUseCase.items);
            pagination.grupoAcessoUsuarioSetter.setTotalItems(getGrupoAcessoUsuarioUseCase.totalItems);

            if (idsGrupoAcesso && idsGrupoAcesso.length > 0) {
                setGrupoAcessoSelectionModel({ type: 'include', ids: new Set(idsGrupoAcesso) });
            }
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar grupos de acesso do usuário!'));
        } finally {
            setPageLoading(false);
        }
    }, [
        getGrupoAcessoUsuarioUseCase,
        pagination,
        pushErrorNotification,
        setPageLoading,
    ]);


    const loadUsuario = useCallback(async (idUsuario: number) => {

        try {
            setPageLoading(true);

            const usuario = await getUsuarioByIdUseCase.execute(idUsuario);

            hookForm.setValue('idUsuario', usuario?.idUsuario ?? 0);
            hookForm.setValue('nome', usuario?.nome ?? "");
            hookForm.setValue('login', usuario?.login ?? "");
            hookForm.setValue('email', usuario?.email ?? "");
            hookForm.setValue('centroCustoRestrito', usuario?.centroCustoRestrito ?? false);
            hookForm.setValue('idFuncionario', usuario?.idFuncionario ?? null);
            hookForm.setValue('bloqueado', usuario?.bloqueado ? 1 : 0);
            hookForm.setValue('isConfigured', usuario?.isConfigured ?? false);

            if (usuario.gruposUsuario) {
                hookForm.setValue('gruposUsuario',
                    [...usuario?.gruposUsuario.values()].map(x => ({ idGrupoAcesso: typeof x === 'number' ? x : Number(x) })));
            }

            if (usuario.restricoesCentroCusto) {
                hookForm.setValue('restricoesCentroCusto',
                    [...usuario?.restricoesCentroCusto.values()].map(x => ({ idCentroCusto: typeof x === 'number' ? x : Number(x) })));
            }

            setSelectedUsuario(usuario);
            await loadGrupoAcessoUsuario({ page: 1, rowsPerPage: 100 },
                usuario.gruposUsuario ? [...usuario?.gruposUsuario.values()].map(x => typeof x === 'number' ? x : Number(x)) : []);

            await loadCentroCustoRestritoLista({ page: 1, rowsPerPage: 100 },
                usuario.restricoesCentroCusto ? [...usuario?.restricoesCentroCusto.values()].map(x => typeof x === 'number' ? x : Number(x)) : []);

        } catch (e: unknown) {
            setSelectedUsuario(undefined);
            pushErrorNotification('Error', processError(e, 'Falha ao buscar usuário!'));
        }
    }, [
        hookForm,
        getUsuarioByIdUseCase,
        loadGrupoAcessoUsuario,
        pushErrorNotification,
        loadCentroCustoRestritoLista,
        setPageLoading,
    ]);

    const loadFuncionarioDropDown = useCallback(async () => {
        try {
            setPageLoading(true);
            const result = await getFuncionariosDropDownUseCase.getFuncionariosDropDown();
            setDdlFuncionario(result.map((x) => ({
                id: x.idFuncionario,
                title: x.nome
            })));

        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar funcionários!'));
        } finally {
            setPageLoading(false);
        }
    }, [
        getFuncionariosDropDownUseCase,
        pushErrorNotification,
        setPageLoading,
    ]);




    //#endregion

    //#region PAGE START

    usePageStartBootstrap(async () => {
        try {

            const params = new URLSearchParams(location.search);
            const paramIdUsuario = params.get('id');
            const locationIdUsuario = paramIdUsuario
                ? parseInt(paramIdUsuario, 10)
                : undefined;

            if (locationIdUsuario !== undefined) {
                setSelectedIdUsuario(locationIdUsuario);
                await loadUsuario(locationIdUsuario);
                await loadFuncionarioDropDown();
            } else {
                hookForm.reset(UsuariosCadastroFormDefaultValues);
                await loadFuncionarioDropDown();
                await loadCentroCustoRestritoLista({ page: 1, rowsPerPage: 100 }, []);
                await loadGrupoAcessoUsuario({ page: 1, rowsPerPage: 100 }, []);
            }
        } catch (e: unknown) {
            setErrorMessage(processError(e, "Falha ao buscar usuário!")[0]);
        }

    });

    //#endregion



    //region NEW USUARIO

    const handleNewUsuario = useCallback(() => {
        navigate(location.pathname, { replace: true, state: {} });
        hookForm.reset(UsuariosCadastroFormDefaultValues);
        setSelectedIdUsuario(undefined);
        setSelectedUsuario(undefined);
        hookForm.reset(UsuariosCadastroFormDefaultValues);
        setGrupoAcessoSelectionModel(undefined);
        setCentroCustoRestritoSelectionModel(undefined);
        setOpenDeleteModal(false);

    }, [navigate, location.pathname, hookForm]);


    const handlePageBack = useCallback(() => {
        navigate('/Usuarios_lista');
    }, [navigate]);


    const handleReload = useCallback(async () => {
        if (selectedIdUsuario) {
            try {
                setLoading(true);
                await loadUsuario(selectedIdUsuario);
            } catch (e: unknown) {
                pushErrorNotification('Error', processError(e, 'Falha ao buscar usuário!'));
            } finally {
                setLoading(false);
            }
        }
    }, [
        selectedIdUsuario,
        loadUsuario,
        pushErrorNotification,
    ]);

    //#endregion






    //#region CADASTRO/UPDATE

    const handleUsuarioCadastroSubmit = useCallback(async (form: UsuariosCadastroForm) => {
        try {
            setSaving(true);
            if (form.senha !== null && form.senha.length > 0) {
                if (form.senha !== form.confirmarSenha) {
                    pushWarnNotification("Atenção", "A senha e a confirmação de senha não coincidem!");
                    return;
                }
            }
            if (selectedIdUsuario) {
                if (form.gruposUsuario && form.gruposUsuario.length === 0) {
                    pushWarnNotification("Atenção", "O usuário deve possuir ao menos um grupo de acesso!");
                    return;
                }
                await updateUsuarioUseCase.execute(
                    selectedIdUsuario,
                    form.nome,
                    form.login,
                    form.senha,
                    form.email,
                    form.centroCustoRestrito,
                    form.bloqueado === 1 ? true : false,
                    form.isConfigured,
                    form.idFuncionario ?? undefined,

                    form.gruposUsuario ? form.gruposUsuario.map(x => x.idGrupoAcesso) : [],
                    form.restricoesCentroCusto ? form.restricoesCentroCusto.map(x => x.idCentroCusto) : []
                );

                if (user.idUsuario === selectedIdUsuario) {
                    pushInfoNotification("Sucesso", "Usuário alterado com sucesso!", { isAutoFinish: true });
                    user.forcedUserLogout();
                    return;
                }
                await loadUsuario(selectedIdUsuario);
                pushInfoNotification("Sucesso", "Usuário alterado com sucesso!", { isAutoFinish: true });

            } else {
                if (form.gruposUsuario && form.gruposUsuario.length === 0) {
                    pushWarnNotification("Atenção", "O usuário deve possuir ao menos um grupo de acesso!");
                    return;
                }
                if (form.senha === null || form.senha.length === 0) {
                    pushErrorNotification("Atenção", "O usuário deve possuir uma senha  !");
                    return;
                }
                const newIdUsuario = await createUsuarioUseCase.execute(
                    form.nome,
                    form.login,
                    form.senha,
                    form.email,
                    form.centroCustoRestrito,
                    form.bloqueado === 1 ? true : false,
                    form.idFuncionario ?? undefined,
                    form.isConfigured,

                    form.gruposUsuario ? form.gruposUsuario.map(x => x.idGrupoAcesso) : [],
                    form.restricoesCentroCusto ? form.restricoesCentroCusto.map(x => x.idCentroCusto) : []
                );

                navigate(`${location.pathname}?id=${newIdUsuario}`, { replace: true, state: {} });
                pushInfoNotification("Sucesso", "Usuário criado com sucesso!", { isAutoFinish: true });
            }
        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, "Falha ao salvar usuário!"), { isAutoFinish: true });
        } finally {
            setSaving(false);
        }
    }, [
        selectedIdUsuario,
        updateUsuarioUseCase,
        createUsuarioUseCase,
        location.pathname,
        user,
        loadUsuario,
        navigate,
        pushInfoNotification,
        pushWarnNotification,
        pushErrorNotification,
    ]);

    //#endregion




    //#region EXCLUSÃO

    const handleUsuarioDeleteOpenState = useCallback((openState: boolean) => {
        if (selectedIdUsuario === undefined) {
            pushWarnNotification("Atenção", "Nenhum usuário selecionado para exclusão!", { isAutoFinish: true });
            return;
        }
        if (openState) {
            setOpenDeleteModal(openState)
        } else {
            setOpenDeleteModal(openState)
        }
    }, [selectedIdUsuario, pushWarnNotification]);


    const handleConfirmarDeletarUsuario = useCallback(async () => {
        try {
            if (selectedIdUsuario) {
                await deleteUsuarioUseCase.execute(selectedIdUsuario);
                pushInfoNotification("Sucesso", "Usuário deletado com sucesso!", { isAutoFinish: true });
                navigate('/Usuarios_lista');
            }
        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, "Falha ao deletar usuário!"), { isAutoFinish: true });
        } finally {
            setOpenDeleteModal(false);
            setSelectedIdUsuario(undefined);

        }
    }, [
        selectedIdUsuario,
        deleteUsuarioUseCase,
        pushInfoNotification,
        pushErrorNotification,
        navigate]);


    //#endregion





    //#region TABELA GRUPO ACESSO USUARIO
    const handleGrupoAcessoSelectionModelChange = useCallback((selectionModels: GridRowSelectionModel) => {
        hookForm.setValue('gruposUsuario', [...selectionModels.ids.values()].map(x => ({ idGrupoAcesso: typeof x === 'number' ? x : Number(x) })));
        setGrupoAcessoSelectionModel(selectionModels);
    }, [hookForm]);

    const handleGrupoAcessoPageChange = useCallback(async (page: number) => {
        try {
            setLoading(true);
            useUsuariosCadastroPagination.getState().grupoAcessoUsuarioSetter.setPage(page + 1);
            await loadGrupoAcessoUsuario({ page: page + 1 });
        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, "Falha ao buscar os grupos de acesso usuario!"), { isAutoFinish: true });
        } finally {
            setLoading(false);
        }
    }, [
        loadGrupoAcessoUsuario,
        pushErrorNotification,
    ]);

    const handleGrupoAcessoRowsPerPageChange = useCallback(async (rowsPerPage: number) => {
        try {
            setLoading(true);
            useUsuariosCadastroPagination.getState().grupoAcessoUsuarioSetter.setPage(1);
            useUsuariosCadastroPagination.getState().grupoAcessoUsuarioSetter.setRowsPerPage(rowsPerPage);
            await loadGrupoAcessoUsuario({ rowsPerPage, page: 1 });
        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, "Falha ao buscar os grupos de acesso usuario!"), { isAutoFinish: true });
        } finally {
            setLoading(false);
        }
    }, [
        loadGrupoAcessoUsuario,
        pushErrorNotification,
    ]);

    //#endregion




    //#region TABELA CENTRO CUSTO RESTRIÇÃO USUARIO
    const handleCentroCustoRestritoSelectionModelChange = useCallback((selectionModels: GridRowSelectionModel) => {
        hookForm.setValue('restricoesCentroCusto', [...selectionModels.ids.values()].map(x => ({ idCentroCusto: typeof x === 'number' ? x : Number(x) })));
        setCentroCustoRestritoSelectionModel(selectionModels);

    }, [hookForm]);

    const handleCentroCustoRestritoPageChange = useCallback(async (page: number) => {
        try {
            setLoading(true);
            useUsuariosCadastroPagination.getState().centroCustoRestricaoUsuarioSetter.setPage(page + 1);
            await loadCentroCustoRestritoLista({ page: page + 1 });
        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, "Falha ao buscar os centros de custo restrito usuario!"), { isAutoFinish: true });
        } finally {
            setLoading(false);
        }
    }, [

        loadCentroCustoRestritoLista,
        pushErrorNotification,
    ]);

    const handleCentroCustoRestritoRowsPerPageChange = useCallback(async (rowsPerPage: number) => {
        try {
            setLoading(true);
            useUsuariosCadastroPagination.getState().centroCustoRestricaoUsuarioSetter.setPage(1);
            useUsuariosCadastroPagination.getState().centroCustoRestricaoUsuarioSetter.setRowsPerPage(rowsPerPage);
            await loadCentroCustoRestritoLista({ rowsPerPage, page: 1 });
        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, "Falha ao buscar os centros de custo restricao usuario!"), { isAutoFinish: true });
        } finally {
            setLoading(false);
        }
    }, [
        loadCentroCustoRestritoLista,
        pushErrorNotification,
    ]);

    //#endregion


    //#region HISTORICO

    const handleOpenChangeHistory = useCallback((
        idTabela: number | null,
        tipoTabela: LogTipoTabela | null,
    ) => {
        if (idTabela && tipoTabela) {
            changeHistory.handleOpenChangeHistory(idTabela, tipoTabela, false);
        } else {
            if (true) {
                changeHistory.handleOpenChangeHistory(selectedIdUsuario ?? 0, LogTipoTabela.USUARIO, true);
            }
        }
    }, [
        selectedIdUsuario,
        changeHistory,
    ]);

    //#endregion


    return {
        loading,
        saving,
        errorMessage,
        openDeleteModal,
        changeHistory,
        ddlFuncionario,
        selectedUsuario,
        centroCustoRestrito,
        hookForm,
        selectedIdUsuario,
        senha,
        grupoAcessoUsuarioLista,
        restricaoCentroCustoUsuarioLista,
        grupoAcessoSelectionModel,
        centroCustoRestritoSelectionModel,
        pagination,
        statusOptions,

        handleCentroCustoRestritoPageChange,
        handleCentroCustoRestritoRowsPerPageChange,
        handleCentroCustoRestritoSelectionModelChange,
        handleGrupoAcessoSelectionModelChange,
        handleGrupoAcessoPageChange,
        handleGrupoAcessoRowsPerPageChange,
        setCentroCustoRestritoSelectionModel,
        setGrupoAcessoSelectionModel,
        handleReload,
        handlePageBack,
        handleNewUsuario,
        handleUsuarioDeleteOpenState,
        handleConfirmarDeletarUsuario,
        handleOpenChangeHistory,
        handleUsuarioCadastroSubmit: hookForm.handleSubmit(handleUsuarioCadastroSubmit),

    };

}


